import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-221c329b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "dashboard-chart-card" }
const _hoisted_2 = { class: "dashboard-chart-card__header" }
const _hoisted_3 = { class: "dashboard-chart-card__title" }
const _hoisted_4 = {
  key: 0,
  class: "dashboard-chart-card__empty"
}
const _hoisted_5 = {
  key: 1,
  class: "dashboard-chart-card__body"
}
const _hoisted_6 = { class: "dashboard-chart-card__canvas-wrap" }
const _hoisted_7 = { class: "dashboard-chart-card__legend" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "dashboard-chart-card__legend-label" }
const _hoisted_10 = { class: "dashboard-chart-card__legend-count" }

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import {
  Chart as ChartJS,
  ArcElement,
  DoughnutController,
  Tooltip,
  Legend,
} from "chart.js";
import type {
  DashboardChartItem,
  DashboardDetailType,
} from "@/types/dashboard/DashboardTypes";
import { t } from "@/i18n";
import { currentTheme } from "@/utils/theme";


export default /*#__PURE__*/_defineComponent({
  props: {
    title: null,
    items: null,
    detailType: null
  },
  emits: ["open-details"],
  setup(__props: any, { emit }: { emit: ({
  (
    event: "open-details",
    type: DashboardDetailType,
    title: string,
    templateId?: string
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  title: string;
  items: DashboardChartItem[];
  detailType: "INCOMING" | "OUTGOING";
}

ChartJS.register(ArcElement, DoughnutController, Tooltip, Legend);





const canvasRef = ref<HTMLCanvasElement | null>(null);
let chartInstance: ChartJS<"doughnut"> | null = null;

const colors = [
  "#3B82F6",
  "#22C55E",
  "#A855F7",
  "#F97316",
  "#EF4444",
  "#06B6D4",
];

const total = computed(() =>
  props.items.reduce((sum, item) => sum + (item.count ?? 0), 0)
);

const chartData = computed(() => ({
  labels: props.items.map((item) => item.label ?? ""),
  datasets: [
    {
      data: props.items.map((item) => item.count ?? 0),
      backgroundColor: props.items.map(
        (_, index) => colors[index % colors.length]
      ),
      borderWidth: 0,
      hoverOffset: 4,
    },
  ],
}));

const centerTextPlugin = {
  id: "dashboard-center-text",
  beforeDraw(chart: ChartJS) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) {
      return;
    }

    const arc = meta.data[0] as { x: number; y: number };
    ctx.save();
    ctx.font = "38px Inter, system-ui, sans-serif";
    ctx.fillStyle = currentTheme.value === "dark" ? "#e5edf8" : "#111827";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(total.value), arc.x, arc.y - 9, 120);
    ctx.font = "12px Inter, system-ui, sans-serif";
    ctx.fillStyle = currentTheme.value === "dark" ? "#a9b9cf" : "#8B98AA";
    ctx.fillText(t("dashboard.chart.total"), arc.x, arc.y + 20, 120);
    ctx.restore();
  },
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "86%",
  layout: {
    padding: 6,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label(context: { label?: string; parsed?: number }) {
          const label = context.label || "";
          const value = context.parsed || 0;
          return `${label}: ${value}`;
        },
      },
    },
  },
  onClick(_event: unknown, elements: Array<{ index: number }>) {
    const selectedIndex = elements[0]?.index;
    if (selectedIndex === undefined) return;
    const item = props.items[selectedIndex];
    if (item) {
      openDetails(item.templateId, item.label);
    }
  },
};

function openDetails(templateId?: string | null, label?: string | null) {
  emit(
    "open-details",
    props.detailType,
    label ? `${props.title}: ${label}` : props.title,
    templateId ?? undefined
  );
}

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}

function renderChart() {
  destroyChart();

  if (!canvasRef.value || !props.items.length) {
    return;
  }

  chartInstance = new ChartJS(canvasRef.value, {
    type: "doughnut",
    data: chartData.value,
    options: chartOptions,
    plugins: [centerTextPlugin],
  });
}

async function scheduleRender() {
  await nextTick();
  renderChart();
}

onMounted(scheduleRender);

watch(() => props.items, scheduleRender, { deep: true, flush: "post" });
watch(currentTheme, scheduleRender, { flush: "post" });

onBeforeUnmount(destroyChart);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(__props.title), 1),
      _createElementVNode("button", {
        type: "button",
        class: "dashboard-chart-card__details",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (openDetails()))
      }, _toDisplayString(_ctx.$t("dashboard.details.viewDocuments")), 1)
    ]),
    (!__props.items.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t("dashboard.chart.noData")), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("canvas", {
              ref: (_value, _refs) => {
                _refs['canvasRef'] = _value
                canvasRef.value = _value
              }
            }, null, 512)
          ]),
          _createElementVNode("ul", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.items, (item, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: `${item.code ?? item.templateId ?? index}`
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "dashboard-chart-card__legend-item",
                  onClick: ($event: any) => (openDetails(item.templateId, item.label))
                }, [
                  _createElementVNode("span", {
                    class: "dashboard-chart-card__legend-dot",
                    style: _normalizeStyle({ backgroundColor: colors[index % colors.length] })
                  }, null, 4),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(item.label), 1),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(item.count ?? 0), 1)
                ], 8, _hoisted_8)
              ]))
            }), 128))
          ])
        ]))
  ]))
}
}

})